import { create } from 'zustand';
import type {
  LaunchMode,
  SnapValue,
  StopPolicy,
  StudioSnapshot,
  StudioView,
} from '@melodinai/types';
import { getEngine } from '../audio/engineBridge';

interface StudioStore extends StudioSnapshot {
  hydrated: boolean;
  hydrateFromEngine: () => void;
  play: () => void;
  pause: () => void;
  stop: () => void;
  togglePlay: () => void;
  toggleRecord: () => void;
  setBpm: (bpm: number) => void;
  setView: (view: StudioView) => void;
  setSessionQuantize: (q: SnapValue) => void;
  setStopPolicy: (p: StopPolicy) => void;
  setLaunchMode: (m: LaunchMode) => void;
  toggleSessionCapture: () => void;
  stopAllSessionClips: () => void;
  launchScene: (sceneIdx: number) => void;
  launchTrack: (trackId: string) => void;
  queueClip: (trackId: string, sceneIdx: number) => void;
  stopTrack: (trackId: string) => void;
  addTrack: (opts?: { name?: string; type?: 'audio' | 'midi' | 'drum' | 'group' | 'return' | 'master' }) => void;
  selectTrack: (trackId: string) => void;
}

export const useStudioStore = create<StudioStore>((set, get) => ({
  hydrated: false,
  transport: {
    bpm: 120,
    timeSig: [4, 4],
    playing: false,
    recording: false,
    loopOn: false,
    metronome: false,
    playheadBeat: 0,
    loopStart: 0,
    loopEnd: 8,
  },
  project: {
    name: 'Untitled Project',
    view: 'session',
    snap: 'beat',
    pixelsPerBeat: 60,
    activeDetailTab: 'fx',
  },
  tracks: [],
  session: {
    quantize: 'bar',
    sceneCount: 8,
    stopPolicy: 'exclusive',
    defaultLaunchMode: 'trigger',
    capture: false,
    slots: {},
    queuedKeys: [],
    playingByTrack: {},
  },
  selectedTrackId: null,
  selectedAudioClip: null,
  selectedMidiClip: null,

  hydrateFromEngine: () => {
    const engine = getEngine();
    const apply = (snap: StudioSnapshot) => {
      set({
        ...snap,
        hydrated: true,
      });
    };
    apply(engine.getSnapshot());
    engine.subscribe(apply);
  },

  play: () => getEngine().play(),
  pause: () => getEngine().pause(),
  stop: () => getEngine().stop(),
  togglePlay: () => {
    const { transport } = get();
    if (transport.playing) getEngine().pause();
    else getEngine().play();
  },
  toggleRecord: () => getEngine().toggleRecord(),
  setBpm: (bpm) => getEngine().setBpm(bpm),
  setView: (view) => getEngine().setView(view),
  setSessionQuantize: (q) => getEngine().setSessionQuantize(q),
  setStopPolicy: (p) => getEngine().setStopPolicy(p),
  setLaunchMode: (m) => getEngine().setLaunchMode(m),
  toggleSessionCapture: () => getEngine().toggleSessionCapture(),
  stopAllSessionClips: () => getEngine().stopAllSessionClips(),
  launchScene: (i) => getEngine().launchScene(i),
  launchTrack: (id) => getEngine().launchTrack(id),
  queueClip: (tid, s) => getEngine().queueClip(tid, s),
  stopTrack: (id) => getEngine().stopTrack(id),
  addTrack: (opts) => getEngine().addTrack(opts),
  selectTrack: (id) => getEngine().selectTrack(id),
}));
